import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { API_CONFIG, APP_CONFIG, ADMIN_ROLES } from '../config/authConfig';
import { LoginCredentials, AuthResponse, AuthHookResult } from '../types/authTypes';
import { clearUserSessionFully, saveUserSession } from '../utils/sessionManager';

interface PendingLoginData {
  credentials: LoginCredentials;
  response: AuthResponse;
  rememberMe: boolean;
}

// Roles that require password expiration check
const ROLES_REQUIRING_PASSWORD_CHECK = ['user', 'storage'];

// Check if password needs to be changed (null or older than 30 days)
const isPasswordExpired = (lastPassUpd: string | null | undefined, role: string): boolean => {
  if (!ROLES_REQUIRING_PASSWORD_CHECK.includes(role)) {
    return false;
  }

  if (!lastPassUpd) return true;

  const lastUpdate = new Date(lastPassUpd);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24));

  return diffDays > 30;
};

export const useStandardAuth = (): AuthHookResult => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState<boolean>(false);
  const [pendingLoginData, setPendingLoginData] = useState<PendingLoginData | null>(null);
  const navigate = useNavigate();

  const completeLogin = (cleanLogin: string, response: AuthResponse, rememberMe: boolean) => {
    // Очищаем данные прежней учетной записи (права и флаги профиля тоже)
    clearUserSessionFully();

    // Запоминание логина в cookies
    if (rememberMe) {
      Cookies.set('rememberedLogin', cleanLogin, { expires: APP_CONFIG.REMEMBER_ME_EXPIRES_DAYS });
    } else {
      Cookies.remove('rememberedLogin');
    }

    // Сохраняем сессию в localStorage
    saveUserSession(response);

    // Перенаправляем пользователя в зависимости от целевого рабочего интерфейса роли
    const targetInterface = response.targetInterface || (
      ['superadmin', 'admin', 'moder', 'view', 'guide', 'km', 'to', 'full-report'].includes(response.role) ? 'admin' : 'dashboard'
    );
    navigate(targetInterface === 'admin' ? '/admin' : '/dashboard');
  };

  const handleLogin = async (credentials: LoginCredentials, rememberMe: boolean) => {
    setLoading(true);
    setError(null);

    // Автоматический перевод в нижний регистр и тримминг
    const cleanLogin = (credentials.login || '').trim().toLowerCase();
    const cleanPassword = credentials.password || '';
    const clientVersion = process.env.REACT_APP_VERSION || APP_CONFIG.VERSION || '1.0.3';

    try {
      // Посылаем запрос на новый изолированный эндпоинт v2
      const response = await axios.post<AuthResponse>(
        `${API_CONFIG.AUTH_V2_URL}/login`,
        {
          login: cleanLogin,
          password: cleanPassword,
          version: clientVersion
        }
      );

      if (response.data.success) {
        // Проверка необходимости обязательной смены пароля (>30 дней)
        if (isPasswordExpired(response.data.lastPassUpd, response.data.role)) {
          setPendingLoginData({
            credentials: { login: cleanLogin, password: cleanPassword },
            response: response.data,
            rememberMe
          });
          setRequiresPasswordChange(true);
          setLoading(false);
          return { success: true, requiresPasswordChange: true };
        }

        completeLogin(cleanLogin, response.data, rememberMe);
        return { success: true };
      } else {
        const errorMessage = response.data.message || 'Неправильный логин или пароль';
        setError(errorMessage);

        // Если не совпадает версия фронтенда с сервером - очищаем кэши и перезагружаем
        if (response.data.versionMismatch) {
          if ('caches' in window) {
            caches.keys().then((names) => {
              for (let name of names) {
                caches.delete(name);
              }
            });
          }
          setTimeout(() => {
            window.location.reload();
          }, 2000);
          return { success: false, error: errorMessage, versionMismatch: true };
        }

        return { success: false, error: errorMessage };
      }
    } catch (err: any) {
      console.error('Ошибка при авторизации:', err);
      const errorMessage = err.response?.data?.message || 'Ошибка подключения к серверу. Проверьте подключение к сети.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const onPasswordChangeComplete = () => {
    if (pendingLoginData) {
      localStorage.setItem('forceProfileCheck', 'true');

      completeLogin(
        pendingLoginData.credentials.login,
        pendingLoginData.response,
        pendingLoginData.rememberMe
      );
      setPendingLoginData(null);
      setRequiresPasswordChange(false);
    }
  };

  const clearError = () => setError(null);

  return {
    loading,
    error,
    handleLogin,
    clearError,
    requiresPasswordChange,
    pendingLoginData,
    onPasswordChangeComplete
  };
};
